import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import ContentBlock from "@/components/wpl/content-block/src";
import Image from "@/components/wpl/image/src";
import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps } from "@/libraries/wpl/foundations/src";

const sectionStyle = css({
    "display": "flex",
    "flexDirection": "column",
    "padding": `0 0 ${token("space.400")}`,
    "color": "#091e42",
    ".text-section": {
        padding: `0 ${token("space.200")} ${token("space.200")}`,
        margin: "auto",
        h2: {
            "fontWeight": 500,
            "textAlign": "center",
            "color": "#091E42",
            "marginBlockStart": 0,
            "marginBlockEnd": "16px",
            "@media (max-width: 47.99rem)": {
                fontSize: "2rem",
                lineHeight: "2.375rem"
            },
            "@media (min-width: 48rem) and (max-width: 63.99rem)": {
                fontSize: "2.25rem",
                lineHeight: "2.625rem"
            },
            "@media (min-width: 64rem)": {
                fontSize: "2.5rem",
                lineHeight: "3rem"
            }
        },
        p: {
            "textAlign": "center",
            "margin": 0,
            "fontWeight": 400,
            "color": "#091E42",
            "@media (max-width: 63.99rem)": {
                fontSize: "1.125rem",
                lineHeight: "1.625rem"
            },
            "@media (min-width: 64rem)": {
                fontSize: "1.25rem",
                lineHeight: "1.75rem"
            }
        },
        [media.above.sm]: {
            maxWidth: "768px"
        },
        [media.only.sm]: {
            maxWidth: "83.33%"
        },
        [media.below.sm]: {
            maxWidth: "100%"
        }
    }
});

const IntegrationsSection = () => {
    const intl = useIntl();

    const linkText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.apps.visit-marketplace",
        defaultMessage: "Visit the Marketplace"
    });

    const integrationsLinks: IntegrationsLinkProps[] = [
        {
            href: useLocalizedHref("/software/confluence/jira-integration"),
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:c5c9c69d-d6ce-43d2-98f6-5e0fe0f2498e/logos-Jira-blue.svg?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.jira-logo-alt",
                defaultMessage:
                    "The Jira logo which resembles three arrowheads pointing up and to the right."
            }),
            width: "115px"
        },
        {
            href: "https://trello.com",
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:b30cea8b-b00c-4d2d-af70-1b75d7a554ce/trello-logo.svg?cdnVersion=1263",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.trello-logo-alt",
                defaultMessage:
                    "Trello logo which resembles a trello board with two columns."
            }),
            width: "167px"
        },
        {
            href: "https://marketplace.atlassian.com/apps/1219518/confluence-cloud-for-slack-official?hosting=cloud&tab=overview",
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:fabfffaa-4eff-4e7f-a763-a29f5de3c021/Slack_RGB.svg?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.slack-logo-alt",
                defaultMessage: "Slack logo"
            }),
            width: "154px"
        },
        {
            href: useLocalizedHref(
                "/software/confluence/microsoft-teams-integration"
            ),
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:cf215f9c-fbe5-4449-84fa-cafb47ca76b8/Microsoft_Teams_Logo_2x.png?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.microsoft-teams-logo-alt",
                defaultMessage: "Microsoft Teams logo"
            }),
            width: "181px"
        },
        {
            href: "https://marketplace.atlassian.com/apps/1221692/figma-for-confluence?tab=overview&hosting=cloud",
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:3a696f17-0629-4a92-895b-cdaf3fdd3d08/figma-logo-full.svg?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.figma-logo-alt",
                defaultMessage: "Figma logo"
            }),
            width: "124px"
        },
        {
            href: "https://marketplace.atlassian.com/apps/1216783/integration-for-dropbox-confluence?tab=overview&hosting=cloud",
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:716ebd30-7743-4e96-a32b-621be8de86af/dropbox.svg?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.dropbox-logo-alt",
                defaultMessage: "Dropbox logo"
            }),
            width: "168px"
        },
        {
            href: "https://marketplace.atlassian.com/apps/1216978/google-drive-and-docs-for-confluence?tab=overview&hosting=cloud",
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:b90ddb4c-5cb6-48bd-9661-0feaa78a703d/google_drive_wordmark.svg?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.google-drive-logo-alt",
                defaultMessage: "Google Drive logo"
            }),
            width: "166px"
        },
        {
            href: "https://marketplace.atlassian.com/apps/1216591/box-for-confluence?hosting=cloud&tab=overview",
            src: "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:61dc7b6e-29f5-4a55-9316-e5662d82462d/box-logo.svg?cdnVersion=1265",
            alt: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.integrations.box-logo-alt",
                defaultMessage: "box logo"
            }),
            width: "89px"
        }
    ];

    const integrationsLinkDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "integrationsSection",
        "data-event": "clicked",
        "data-label": linkText,
        "data-label-english": "Visit the Marketplace"
    };

    return (
        <section css={sectionStyle}>
            <div>
                <ContentBlock
                    headerType="h2"
                    headerText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.apps.headerText",
                        defaultMessage: "Use Confluence with your favorite apps"
                    })}
                    bodyText={intl.formatMessage({
                        id: "ConfluenceSoftwarePageTemplate.apps.bodyText",
                        defaultMessage:
                            "With 3000+ integrations, we’re sure you’ll find the right tool for the job."
                    })}
                    isCentered={true}
                />
            </div>
            <div
                css={css({
                    display: "flex",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    margin: "auto",
                    maxWidth: "1200px"
                })}
            >
                {integrationsLinks.map((linkProps) => (
                    <IntegrationsLink {...linkProps} key={linkProps.href} />
                ))}
            </div>
            <div
                css={css({
                    display: "flex",
                    justifyContent: "center",
                    padding: "32px 16px 16px"
                })}
            >
                <Link
                    href="//marketplace.atlassian.com/addons/app/confluence"
                    text={linkText}
                    type="basic-underline"
                    customLinkColor="#0052cc"
                    doesArrowAnimate={true}
                    dataEventProps={integrationsLinkDataEventProps}
                />
            </div>
        </section>
    );
};

interface IntegrationsLinkProps {
    href: string;
    src: string;
    alt: string;
    width: string;
}

const IntegrationsLink = ({ href, src, alt, width }: IntegrationsLinkProps) => {
    const integrationsLinkDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-container": "integrationsSection",
        "data-event-component": "linkButton",
        "data-event": "clicked"
    };

    return (
        <div
            css={css({
                padding: "16px",
                [media.above.sm]: {
                    width: "25%"
                },
                [media.only.sm]: {
                    width: "41.66%"
                },
                [media.below.sm]: {
                    width: "50%"
                }
            })}
        >
            <a
                css={css({
                    "&:hover > div": {
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.25)"
                    }
                })}
                href={href}
                target="_blank"
                rel="noreferrer"
                {...integrationsLinkDataEventProps}
            >
                <div
                    css={css({
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "#fafbfc",
                        borderRadius: "3%",
                        boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.1)",
                        padding: "40px",
                        height: "100%",
                        maxWidth: "100%",
                        transition: "box-shadow 200ms ease-in"
                    })}
                >
                    <div
                        css={css({
                            display: "flex",
                            width: width,
                            maxWidth: "100%"
                        })}
                    >
                        <Image src={src} alt={alt} />
                    </div>
                </div>
            </a>
        </div>
    );
};

export default IntegrationsSection;
